/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import React, { useState, useEffect } from 'react';

import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get, values, sortBy } from 'lodash';

import store from '../../../root/store';
import Element from '../../../components/element';
import Button from '../../../components/Button';
import Flex from '../../../components/flex';
import Text from '../../../components/text';
import useStyles from './style';
import useToast from '../../../components/Toast/useToast';
import { colors } from '../../../config/theme';
import { apiRequest } from '../../../api/actions';
import { ReactComponent as IconMentalHealth } from '../../../assets/icons/IconMentalHealth.svg';
import { ReactComponent as IconFamilyHistory } from '../../../assets/icons/IconFamilyHistory.svg';
import { ReactComponent as IconSocialHistory } from '../../../assets/icons/IconSocialHistory.svg';
import { ReactComponent as IconMedicalHistory } from '../../../assets/icons/IconMedicalHistory.svg';
import { ReactComponent as IconAllergiesAndMedication } from '../../../assets/icons/IconAllergiesAndMedication.svg';
import { ReactComponent as IconSleepQuality } from '../../../assets/icons/IconSleepQuality.svg';
import { ReactComponent as IconPhysicalActivity } from '../../../assets/icons/IconPhysicalActivityNew.svg';
import { ReactComponent as IconNutrition } from '../../../assets/icons/IconNutrition.svg';

import { ReactComponent as IconBalance } from '../../../assets/icons/IconBalance.svg';
import { ReactComponent as IconBlood } from '../../../assets/icons/IconBlood.svg';
import { ReactComponent as IconBodyComp } from '../../../assets/icons/IconBodyComp.svg';
// import { ReactComponent as IconBodySymmetry } from '../../../assets/icons/IconBodySymmetry.svg';
import { ReactComponent as IconCognition } from '../../../assets/icons/IconCognition.svg';
import { ReactComponent as IconDemographics } from '../../../assets/icons/IconDemographics.svg';
import { ReactComponent as IconDexteritRight } from '../../../assets/icons/IconDexteritRight.svg';
import { ReactComponent as IconHeight } from '../../../assets/icons/IconHeight.svg';
import { ReactComponent as IconStrength } from '../../../assets/icons/IconStrength.svg';
import { ReactComponent as IconVitals } from '../../../assets/icons/IconVitals.svg';
import { ReactComponent as IconChevronRight } from '../../../assets/icons/IconChevronRight.svg';

const VisitDetails = () => {
  const location = useLocation()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openSnackbar] = useToast();
  const classes = useStyles();
  const { app } = store.getState().containers
  const user = app.get('user')
  const visit = location?.state?.visit || {}


  var feedbackSubmitted= localStorage.getItem('feedback') || "false"
  const [surveyDetails, setSurveyDetails] = useState({});
  const [healthReportData, setHealthReportData] = useState({});
  const [sortedSurvey, setSortedSurveyDetails] = useState([]);
  const [lastUpdatedDate, setlastUpdatedDetails] = useState([]);
  const [HealthReportList, setHealthReportList] = useState([]);
  const [stationReportList, setStationReportList] = useState([]);

  let allStations = [
    'Demographics',
    'Physical Activity',
    'Sleep',
    'Mental Health',
    'Vital Signs',
    'Nutrition',
    'Body Composition',
    'Blood Draw',
    'Anthropometrics',
    'Dexterity',
    'Balance',
    'Strength',
    'Cognition',
  ]

  const PreVisitAssessmentsList = [
    { name: 'Family History', icon: IconFamilyHistory, value: 0 },
    { name: 'Social History', icon: IconSocialHistory, value: 1 },
    { name: 'Medical History', icon: IconMedicalHistory, value: 2 },
    {
      name: 'Allergies & Medication',
      icon: IconAllergiesAndMedication,
      value: 3
    },
    { name: 'Sleep Quality', icon: IconSleepQuality, value: 4 },
    { name: 'Physical Activity', icon: IconPhysicalActivity, value: 5 },
    { name: 'Nutrition', icon: IconNutrition, value: 6 },
    { name: 'Mental Health', icon: IconMentalHealth, value: 7 }
  ];

  const getStationIcon = (name) => {
    switch (name) {
      case 'Demographics':
        return IconDemographics;
      case 'Physical Activity':
        return IconPhysicalActivity;
      case 'Sleep':
        return IconSleepQuality;
      case 'Mental Health':
        return IconMentalHealth;
      case 'Nutrition':
        return IconNutrition;
      case 'Vital Signs':
      case 'Vitals':
        return IconVitals;
      case 'Blood Draw':
        return IconBlood;
      case 'Anthropometrics':
      case 'Body Scan':
        return IconHeight
      case 'Body Composition':
        return IconBodyComp;
      case 'Cognition':
        return IconCognition;
      case 'Dexterity':
        return IconDexteritRight;
      case 'Strength':
        return IconStrength;
      case 'Balance':
        return IconBalance;
    }
  }


  // Available healthreport stations
  const getHealthReportStations = (availableStations, isDefault = true) => {
    let stationList = []
    let healthReportList = [];

    let availStations = isDefault ? availableStations : availableStations.map(station => station.title)

    for (let i = 0; i < allStations.length; i++) {
      if (availStations.includes(allStations[i]) || availStations.includes('Vitals') ||
        availStations.includes('Body Scan')) {
        // for final consultation
        healthReportList.push({
          name: allStations[i],
          icon: getStationIcon(allStations[i]),
        })
        stationList.push({ name: allStations[i], value: healthReportList.length - 1 })
      } else if (['Demographics', 'Physical Activity', 'Sleep', 'Mental Health', 'Nutrition'].includes(allStations[i])) {
        // for initial consultation
        healthReportList.push({
          name: allStations[i],
          icon: getStationIcon(allStations[i]),
        })
        stationList.push({ name: allStations[i], value: healthReportList.length - 1 })
      }
    }
    setStationReportList(stationList)
    return healthReportList;

  }


  const getVisitDetails = () => {
    const request = {
      method: 'get',
      url: `/me/visit/${visit.uuid}`
    };
    const meta = {
      onSuccess: res => {
        localStorage.removeItem('feedback')
        setHealthReportList(getHealthReportStations(res.data.data.stations, false));
        
        let surveys = values(res.data.data.state.webInterface).slice(0, 9);
        const lastUpdated = values(res.data.data.state.webInterface).slice(-1);
        const value = res.data.data.state.webInterface;
        delete value.surveysLastCompletedAt;
        surveys = sortBy(surveys, ['order']);
        visit.status = res.data.data.status;
        setSurveyDetails(value);
        setSortedSurveyDetails(surveys);
        setlastUpdatedDetails(lastUpdated[0]);
        setHealthReportData(res.data.data.reportCard);
      }
    };

    dispatch(apiRequest(request, meta));
  };

  const getTimeWithAmOrPm = time => {
    // console.log('time', time);
    if (!time) return '';
    const parsedTime = parseInt(time?.split(':')[0]);
    if (parsedTime === 12) {
      return `${time} PM`;
    }
    if (parsedTime === 0) {
      return `12:${time?.split(':')[1]} AM`;
    }
    return parsedTime > 12
      ? `${parsedTime - 12}:${time.split(':')[1]} PM`
      : `${time} AM`;
  };


  const getFormattedDate = () => {
    return `${moment(
      visit.scheduledDate.substring(
        0,
        visit.scheduledDate.indexOf('T')
      )
    ).format('MMMM Do, YYYY')}  at  ${getTimeWithAmOrPm(visit.scheduledTime)}`
  }

    const navigateToFeedback = (pid, vid) => {
    navigate(`/feedback?pid=${pid}&vid=${vid}`);
  };

  const navigateToPastVisits = () => {
    navigate('/landing/pastvisits');
  };
  const navigateToPreAssessments = index => {
    navigate('/landing/pastvisits/preAssessments', {
      replace: true,
      state: {
        visit: visit,
        surveyDetails: surveyDetails,
        sortedSurvey: sortedSurvey,
        lastUpdatedDate: lastUpdatedDate,
        assesmentValue: index
      }
    });
  };

  const navigateToHealthReport = index => {
    navigate('/landing/pastvisits/healthReport', {
      replace: true,
      state: {
        visit: visit,
        assesmentValue: index,
        healthReportData: healthReportData,
        stationList: stationReportList
      }
    });
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };
  const downloadHealthReport = () => {


    const request = {
      method: 'get',
      url: `/visit/${visit.uuid}/reportPdf`
    };
    const meta = {
      onSuccess: res => {
        if (res.data.error?.code) {
          openSnackbar(res.data.message, 'warn');
          return;
        }
        if (!res.data.error) {
          window.open(res.data.data.pdfUri, '_blank');
        } else {
          openSnackbar(
            get(res, 'data.error.message') ||
            'Something went wrong. Please try again.',
            'warn'
          );
        }
      }
    };
    if (visit?.status?.hasFeedback || feedbackSubmitted == 'true') {
      dispatch(apiRequest(request, meta));
    } else {
      navigateToFeedback(user?.id, visit.uuid)
    }
  };
  useEffect(() => {
    feedbackSubmitted= localStorage.getItem('feedback') || "false"
    setHealthReportList(getHealthReportStations(allStations));
    getVisitDetails();
  }, []);
  return (
    <Element>
      <Flex marginBottom="20px">
        <Element onClick={navigateToPastVisits}>
          <Text family="GTAR" size="14px" color={colors.dustyGray}>
            Past Visits
          </Text>
        </Element>
        <Element>

          <Text family="GTAR" size="14px" color={colors.dustyGray}>
            &gt; Visit on {`${moment(
              visit.scheduledDate.substring(
                0,
                visit.scheduledDate.indexOf('T')
              )
            ).format('MMMM Do, YYYY')}`}


          </Text>
        </Element>
      </Flex>
      <Element>
        <Flex
          borderBottom="2px solid #e2e2e2"
          align="center"
          justifyContent="space-between"
        >
          <Element>
            <Text
              family="GTAM"
              size="21px"
              color={colors.mineShaft}
              marginBottom="0px"
            >
              {getFormattedDate()}
            </Text>
            <Text
              family="GTAR"
              size="14px"
              color={colors.dustyGray}
              marginTop="5px"
              marginBottom="15px"
            >
              {get(visit, 'clinic.fullAddress')}
            </Text>
          </Element>
          <Element className={classes.buttonStyle}>
            <Button
              scheme="light"
              style={{
                fontFamily: 'GTAM',
                width: '213px',
                padding: '0px'
              }}
              onClick={downloadHealthReport}
            >
              <Text type="h5" family="GTAM">
                Download Health Report
              </Text>
            </Button>
          </Element>
        </Flex>
      </Element>
      <Element marginTop="20px">
        <Element paddingBottom="20px" borderBottom="1px solid #e2e2e2">
          <Text family="GTAM" size="21px" color={colors.mineShaft}>
            Health Report
          </Text>
        </Element>
        {HealthReportList.length > 0 &&
          HealthReportList.map((each, index) => {
            return (
              <Flex
                align="center"
                justify="space-between"
                borderBottom="1px solid #e2e2e2"
                onClick={() => navigateToHealthReport(index)}
              >
                <Flex align="center">
                  <Element
                    type={each.icon}
                    width="24px"
                    height="24px"
                    marginRight="10px"
                  />
                  <Text
                    family="GTAR"
                    size="21px"
                    color="#222222"
                    paddingTop="10px"
                    paddingBottom="10px"
                    marginBottom="10px"
                  >
                    {each.name}
                  </Text>
                </Flex>
                <Element>
                  <Element type={IconChevronRight} width="24px" height="24px" />
                </Element>
              </Flex>
            );
          })}
      </Element>
      <Element marginTop="50px" marginBottom="50px">
        <Element paddingBottom="20px" borderBottom="1px solid #e2e2e2">
          <Text family="GTAM" size="21px" color={colors.mineShaft}>
            Pre-Visit Assessments
          </Text>
        </Element>
        {PreVisitAssessmentsList.length > 0 &&
          PreVisitAssessmentsList.map((each, index) => {
            return (
              <Flex
                align="center"
                justify="space-between"
                borderBottom="1px solid #e2e2e2"
                onClick={() => navigateToPreAssessments(index)}
              >
                <Flex align="center">
                  <Element
                    type={each.icon}
                    width="24px"
                    height="24px"
                    marginRight="10px"
                  />
                  <Text
                    family="GTAR"
                    size="21px"
                    color="#222222"
                    paddingTop="10px"
                    paddingBottom="10px"
                    marginBottom="10px"
                  >
                    {each.name}
                  </Text>
                </Flex>
                <Element>
                  <Element type={IconChevronRight} width="24px" height="24px" />
                </Element>
              </Flex>
            );
          })}
      </Element>
      <Element marginBottom="100px" className={classes.buttonStyle}>
        <Button
          scheme="light"
          style={{
            fontFamily: 'GTAM',
            fontSize: '16px'
          }}
          onClick={scrollToTop}
        >
          Back to Top
        </Button>
      </Element>
    </Element>
  );
};

export default VisitDetails;
